import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Card, CardBody, Col, Row } from 'reactstrap';
import { UserContext, UserContextProps } from '../../App';
import ButtonWithIcons from '../../components/buttons/ButtonWIthIcons.component';
import { PlusIcon } from '../../components/icon/Icon.component';
import Loader from '../../components/loader/Loader';
import TopBarComponent from '../../components/topBar/TopBar.component';
import { Employee } from '../../models';
import Search from './SearchCases';
import { toTitleCase } from '../../utils/string-utils';
import { CaseTableData } from '../../utils/case-utils';
import { getFlowableCasesForOrganisationCaseTable } from '../../components/tables/case-table-utils';
import { useHistory } from 'react-router';
import useQuery from '../../components/hooks/hooks';
import { get } from '../../utils/graphql-utils';
import { getEmployee } from '../../graphql/queries';
import { PAGE_SIZE } from '../../utils/flowable/flowable-utils';
import DynamicTableLinkable, { RowData } from '../Employee/ViewEmployees/DynamicTableLinkable';

interface CaseViewState {
  showModal: boolean;
  cases: CaseTableData[];
  refinedData: unknown[];
  loading: boolean;
  searchString: string;
  searchResult: Employee[];
}

const ViewCases: React.FC = () => {
  const [state, setState] = useState<CaseViewState>({
    showModal: false,
    cases: [],
    refinedData: [],
    loading: true,
    searchString: '',
    searchResult: [],
  });

  const history = useHistory();
  const query = useQuery();
  const filter = query.get('filter');

  const [caseStatus, setCaseStatus] = useState<string>();
  const currentUser = useContext<Partial<UserContextProps>>(UserContext).currentUser;
  const [pageSize] = useState<number>(PAGE_SIZE);

  const caseStatuses = useMemo(
    () => [
      { label: 'Current', value: 'Current' },
      { label: 'Case Closed', value: 'Case Closed' },
      { label: 'Misconduct', value: 'Misconduct' },
      { label: 'Misconduct Current', value: 'MisconductCurrent' },
      { label: 'Performance', value: 'Performance' },
      { label: 'Performance Current', value: 'PerformanceCurrent' },
      { label: 'Discussions', value: 'Discussions' },
      { label: 'Disciplinary Hearings', value: 'Disciplinary Hearings' },
      { label: 'Current PIP', value: 'Current PIP' },
      { label: 'Current Counselling', value: 'Current Counselling' },
      { label: 'Current Suspension', value: 'Current Suspension' },
    ],
    [],
  );

  const columns: any[] = [
    { key: 'employeeNumber', label: 'Employee No' },
    { key: 'employeeFirstName', label: 'First Name' },
    { key: 'employeeLastName', label: 'Last Name' },
    { key: 'caseNumber', label: 'Case No' },
    { key: 'caseStatus', label: 'Status' },
    { key: 'sanction', label: 'Sanction' },
  ];

  const handleTextChange = (e: any) => {
    try {
      const value = e?.target?.value;
      setState(s => ({ ...s, searchString: typeof value === 'string' ? value.trim() : '' }));
    } catch {
      setState(s => ({ ...s, searchString: '' }));
    }
  };

  const onHandleCaseStatus = (opt: any) => {
    setCaseStatus(opt ? opt.value : '');
  };

  const clearSearchString = () => {
    setState(s => ({ ...s, searchString: '' }));
    setCaseStatus('');
  };

  const loadCases = useCallback(
    async (start: number = 0, append: boolean = false, showLoader: boolean = true): Promise<CaseTableData[]> => {
      if (!currentUser?.organisationId) return [];

      if (showLoader && !append) {
        setState(s => ({ ...s, loading: true }));
      }

      const { rows } = await getFlowableCasesForOrganisationCaseTable(currentUser.organisationId, start, pageSize);

      // Set immediately with placeholder names
      const tempRows = rows.map(row => ({
        ...row,
        employeeNumber: '...',
        employeeFirstName: 'Loading',
        employeeLastName: '',
      }));

      setState(s => ({
        ...s,
        cases: append ? [...s.cases, ...tempRows] : tempRows,
        loading: false,
      }));

      // Enrich in background
      const employeePromises = rows.map(r =>
        get(getEmployee, r.employeeId).then(res => (res.data as any)?.getEmployee),
      );

      const employees = await Promise.all(employeePromises);

      const enrichedRows = rows.map((row, i) => ({
        ...row,
        employeeNumber: employees[i]?.employeeNumber ?? '—',
        employeeFirstName: employees[i] ? toTitleCase(employees[i].firstName, ' ') : '—',
        employeeLastName: employees[i] ? toTitleCase(employees[i].lastName, ' ') : '—',
      }));

      setState(s => {
        const updated = append ? [...s.cases.slice(0, start), ...enrichedRows] : enrichedRows;
        return { ...s, cases: updated };
      });

      return enrichedRows;
    },
    [currentUser, pageSize],
  );

  const Data = useMemo(() => {
    const rows = state.cases.filter(c => c.caseNumber !== 'None');

    const txt = state.searchString.toLowerCase();
    const byText = txt
      ? rows.filter(
          r =>
            r.caseNumber.includes(txt) ||
            r.employeeFirstName.toLowerCase().includes(txt) ||
            r.employeeLastName.toLowerCase().includes(txt),
        )
      : rows;

    if (!caseStatus) return byText;

    const closed = ['Case Closed', 'Dispute Referred'];
    switch (caseStatus) {
      case 'Current':
        return byText.filter(r => !closed.includes(r.caseStatus));
      case 'Case Closed':
        return byText.filter(r => closed.includes(r.caseStatus));
      case 'Misconduct':
        return byText.filter(r => r.caseNumber.includes('M'));
      case 'MisconductCurrent':
        return byText.filter(r => r.caseNumber.includes('M') && !closed.includes(r.caseStatus));
      case 'Performance':
        return byText.filter(r => r.caseNumber.includes('P'));
      case 'PerformanceCurrent':
        return byText.filter(r => r.caseNumber.includes('P') && !closed.includes(r.caseStatus));
      case 'Discussions':
        return byText.filter(r => r.caseStatus.includes('Discussion') && r.caseNumber.includes('M'));
      case 'Disciplinary Hearings':
        return byText.filter(r => r.caseStatus.includes('Hearing') && r.caseNumber.includes('M'));
      case 'Current PIP':
        return byText.filter(r => r.caseStatus.includes('PIP'));
      case 'Current Counselling':
        return byText.filter(r => r.caseStatus.includes('counselling'));
      case 'Current Suspension':
        return byText.filter(r => r.isSuspensionActive && closed.includes(r.caseStatus));
      default:
        return byText.filter(r => r.caseStatus === caseStatus);
    }
  }, [state.cases, state.searchString, caseStatus]);

  useEffect(() => {
    if (currentUser?.organisationId) {
      loadCases(0, false, true);
    }
  }, [currentUser?.organisationId, loadCases]);

  useEffect(() => {
    if (currentUser?.organisationId && state.cases.length === pageSize) {
      const timeout = setTimeout(() => {
        loadCases(pageSize, true, false);
      }, 1000);

      return () => clearTimeout(timeout);
    }
  }, [state.cases.length, currentUser?.organisationId, loadCases, pageSize]);

  useEffect(() => {
    if (caseStatus || !filter) return;
    const map: Record<string, string> = {
      open: 'Current',
      closed: 'Case Closed',
      misconduct: 'MisconductCurrent',
      activepip: 'Current PIP',
      counselling: 'Current Counselling',
      performance: 'PerformanceCurrent',
      hearings: 'Disciplinary Hearings',
      discussions: 'Discussions',
    };
    setCaseStatus(map[filter]);
  }, [filter, caseStatus]);

  return (
    <>
      <TopBarComponent title="Cases" subTitle=" " hideSearch>
        <Row>
          <div style={{ marginRight: 15 }}>
            <Search
              handleTextChange={handleTextChange}
              caseStatuses={caseStatuses}
              selectedCaseStatus={caseStatus}
              onHandleCaseStatus={onHandleCaseStatus}
              onClearSearch={clearSearchString}
            />
          </div>
          <ButtonWithIcons
            title="start new case"
            leftIcon={<PlusIcon />}
            buttonType="btn-bd-purple"
            handleClick={() => history.push('/employees')}
          />
        </Row>
      </TopBarComponent>

      <div className="content">
        {state.loading ? (
          <div className="d-flex justify-content-center mt-5">
            <Loader />
          </div>
        ) : (
          <Row>
            <Col md="12" className="mb-5">
              <Card>
                <CardBody>
                  <DynamicTableLinkable
                    data={(Data as unknown) as RowData[]}
                    columns={columns}
                    linkPrefix="case"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </>
  );
};

export default ViewCases;
